import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ModuleHeader, Text } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { GenerateKeyDialog } from './GenerateKeyDialog'

export function SftpPage() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const [isGenerateOpen, setIsGenerateOpen] = useState(false)
	const [keyToRevoke, setKeyToRevoke] = useState<{ id: string; fingerprint: string } | null>(null)

	const { data, isLoading } = useQuery({
		...trpc.admin.sftp.listKeys.queryOptions({ organizationId: organizationId ?? '' }),
		enabled: !!organizationId,
	})

	const revokeMutation = useMutation({
		...trpc.admin.sftp.revokeKey.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.sftp.listKeys.queryKey({ organizationId: organizationId ?? '' }),
				})
				showToast('SSH key revoked successfully', 'info')
				setKeyToRevoke(null)
			},
			onError: (error) => {
				showToast(`Error revoking key: ${error.message}`, 'error')
			},
		}),
	})

	const handleRevoke = () => {
		if (keyToRevoke && organizationId) {
			revokeMutation.mutate({ keyId: keyToRevoke.id, organizationId })
		}
	}

	const handleCopyConnectionInfo = () => {
		if (!data?.connectionInfo) return
		const { host, port, username } = data.connectionInfo
		const instructions = `SFTP Connection Details:\nHost: ${host}\nPort: ${port}\nUsername: ${username}\n\nConnect using:\nsftp -i <path-to-private-key> ${username}@${host}`
		navigator.clipboard.writeText(instructions).then(
			() => showToast('Connection instructions copied to clipboard', 'info'),
			() => showToast('Failed to copy to clipboard. Please copy manually.', 'error'),
		)
	}

	return (
		<main className='p-6'>
			<ModuleHeader
				breadcrumb={[{ label: 'Organizations', to: '/organizations' }, { label: 'SFTP Keys' }]}
				title='SFTP Keys'
			/>

			{data?.connectionInfo && (
				<div className='bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6'>
					<Text variant='medium' weight='semibold' className='mb-2'>
						SFTP Connection Details
					</Text>
					<div className='grid grid-cols-1 md:grid-cols-3 gap-2 mb-3'>
						<div>
							<Text variant='small' className='text-neutral-500'>
								Host
							</Text>
							<Text variant='small' weight='semibold'>
								{data.connectionInfo.host}
							</Text>
						</div>
						<div>
							<Text variant='small' className='text-neutral-500'>
								Port
							</Text>
							<Text variant='small' weight='semibold'>
								{data.connectionInfo.port}
							</Text>
						</div>
						<div>
							<Text variant='small' className='text-neutral-500'>
								Username
							</Text>
							<Text variant='small' weight='semibold'>
								{data.connectionInfo.username}
							</Text>
						</div>
					</div>
					<button
						type='button'
						className='text-sm text-primary-700 hover:text-primary-800 font-medium'
						onClick={handleCopyConnectionInfo}>
						Copy Connection Instructions
					</button>
				</div>
			)}

			<div className='flex items-center justify-between mb-4'>
				<Text variant='h5' weight='semibold'>
					SSH Keys
				</Text>
				<button
					type='button'
					className='px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors'
					onClick={() => setIsGenerateOpen(true)}>
					Generate New Key
				</button>
			</div>

			{isLoading ? (
				<Text variant='small' className='text-neutral-500'>
					Loading...
				</Text>
			) : !data?.keys.length ? (
				<div className='bg-neutral-50 border border-neutral-200 rounded-lg p-8 text-center'>
					<Text variant='medium' className='text-neutral-600'>
						No SFTP keys yet. Generate a key to enable SFTP access for this organization.
					</Text>
				</div>
			) : (
				<div className='bg-white border border-neutral-200 rounded-lg overflow-hidden'>
					<table className='w-full'>
						<thead className='bg-neutral-50 border-b border-neutral-200'>
							<tr>
								<th className='text-left px-4 py-3 text-sm font-medium text-neutral-600'>Label</th>
								<th className='text-left px-4 py-3 text-sm font-medium text-neutral-600'>Fingerprint</th>
								<th className='text-left px-4 py-3 text-sm font-medium text-neutral-600'>Created</th>
								<th className='text-right px-4 py-3 text-sm font-medium text-neutral-600'>Actions</th>
							</tr>
						</thead>
						<tbody>
							{data.keys.map((key) => (
								<tr key={key.id} className='border-b border-neutral-100 last:border-0'>
									<td className='px-4 py-3 text-sm'>{key.label || '-'}</td>
									<td className='px-4 py-3 text-sm font-mono text-neutral-600'>{key.fingerprint}</td>
									<td className='px-4 py-3 text-sm text-neutral-600'>{new Date(key.createdAt).toLocaleDateString()}</td>
									<td className='px-4 py-3 text-right'>
										<button
											type='button'
											className='text-sm text-red-600 hover:text-red-700 font-medium'
											onClick={() => setKeyToRevoke({ id: key.id, fingerprint: key.fingerprint })}>
											Revoke
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{organizationId && (
				<GenerateKeyDialog
					isOpen={isGenerateOpen}
					onClose={() => setIsGenerateOpen(false)}
					organizationId={organizationId}
				/>
			)}

			<DeleteConfirmDialog
				isOpen={!!keyToRevoke}
				onClose={() => setKeyToRevoke(null)}
				onConfirm={handleRevoke}
				isLoading={revokeMutation.isPending}
				fingerprint={keyToRevoke?.fingerprint ?? ''}
			/>
		</main>
	)
}
